<script setup lang="ts">
import StockPriceFormLayout from "@/components/warehouse/StockPriceFormLayout.vue";
import StockPriceGetItemsDialog from "@/components/warehouse/StockPriceGetItemsDialog.vue";
import StockPriceItemsTable from "@/components/warehouse/StockPriceItemsTable.vue";
import { CreateStockPrice } from "@/api/stockPriceApi";
import { useStockPriceForm } from "@/composables/useStockPriceForm";
import { canDoctype } from "@/helper/permissionHelper";
import { presentStockPriceError } from "@/services/stockPriceErrors";
import useToastify from "@/stores/toastify.store";
import type { StockPriceCandidateItem } from "@/type/stockPrice";

/** ⚠️ Inline literal — see the note in `index.vue`. */
definePage({ meta: { permissionKey: "page.warehouse.stock_prices" } });

const { t } = useI18n();
const router = useRouter();
const toast = useToastify();

const saving = ref(false);
const getItemsVisible = ref(false);
const inlineError = ref<{ title: string | null; message: string | null; guidance: string | null } | null>(null);

const formState = useStockPriceForm();

const canCreate = computed(() => canDoctype("Stock Price", "create"));

const existingItemCodes = computed(() =>
  formState.rows.value
    .map(row => String(row.itemCode ?? ""))
    .filter(Boolean));

const canSave = computed(() =>
  canCreate.value && !saving.value && formState.rows.value.length > 0);

function onAddItems(items: StockPriceCandidateItem[]) {
  const { added, skipped } = formState.appendRows(items);

  if (added)
    toast.success({ text: t("alkokh.stockPrice.messages.itemsAdded", { count: added }) });
  else if (skipped)
    toast.warning({ text: t("alkokh.stockPrice.messages.itemsAllPresent") });
}

async function save() {
  if (!canSave.value)
    return;

  inlineError.value = null;

  try {
    const created = await CreateStockPrice(saving, formState.buildPayload());
    if (!created?.id)
      return;

    toast.success({ text: t("alkokh.stockPrice.messages.created") });

    /**
     * ⚠️ Straight to the saved document, not back to the list.
     *
     * Saving is what fills the Action column — until the server has read the
     * prices, nothing on screen says what applying this will do. Dropping the
     * operator on the list would hide the one thing they came here to check.
     */
    await router.push(`/warehouse/stock-prices/${created.id}`);
  } catch (error: any) {
    const presentation = presentStockPriceError(error, t);

    toast.error({ text: presentation.toastMessage });
    inlineError.value = {
      title: presentation.alertTitle,
      message: presentation.alertMessage,
      guidance: presentation.guidance,
    };
  }
}
</script>

<template>
  <StockPriceFormLayout
    :form-state="formState"
    mode="create"
    :loading="saving"
  >
    <template #actions>
      <VBtn
        variant="text"
        to="/warehouse/stock-prices"
      >
        {{ t("alkokh.global.cancel") }}
      </VBtn>

      <VBtn
        color="primary"
        variant="flat"
        prepend-icon="tabler-device-floppy"
        :disabled="!canSave"
        :loading="saving"
        @click="save"
      >
        {{ t("alkokh.global.save") }}
      </VBtn>
    </template>

    <!--
      ⚠️ Save ALONE on a phone — see the note in `[id].vue`. Cancel is dropped
      rather than condensed: the shell's own back arrow already leaves the page,
      and a second button here is what squeezes the title to one character a line.
    -->
    <template #mobileActions>
      <VBtn
        color="primary"
        variant="flat"
        size="small"
        :disabled="!canSave"
        :loading="saving"
        @click="save"
      >
        {{ t("alkokh.global.save") }}
      </VBtn>
    </template>

    <template #alert>
      <VAlert
        v-if="inlineError"
        type="error"
        variant="tonal"
        class="mb-4"
        closable
        @click:close="inlineError = null"
      >
        <template
          v-if="inlineError.title"
          #title
        >
          {{ inlineError.title }}
        </template>

        <div v-if="inlineError.message">
          {{ inlineError.message }}
        </div>
        <div
          v-if="inlineError.guidance"
          class="mt-2 font-weight-medium"
        >
          {{ inlineError.guidance }}
        </div>
      </VAlert>
    </template>

    <template #items>
      <StockPriceItemsTable
        :form-state="formState"
        :loading="saving"
        @add-items="getItemsVisible = true"
      />
    </template>

    <template #aside>
      <!--
        ⚠️ Not a warning, and not a block on saving. A document where nothing is
        priced yet saves perfectly well as a draft — it only refuses to be
        APPLIED. Saying so here is what stops someone filling the whole table,
        pressing save, and then meeting a refusal they could have been warned
        about before they started.
      -->
      <VAlert
        type="info"
        variant="tonal"
        density="comfortable"
      >
        <template #title>
          {{ t("alkokh.stockPrice.effect.draftTitle") }}
        </template>
        {{ t("alkokh.stockPrice.effect.draftDesc") }}
      </VAlert>

      <VAlert
        type="info"
        variant="tonal"
        density="comfortable"
      >
        {{ t("alkokh.stockPrice.items.zeroHint") }}
      </VAlert>
    </template>

    <StockPriceGetItemsDialog
      v-model="getItemsVisible"
      :existing-item-codes="existingItemCodes"
      @add="onAddItems"
    />
  </StockPriceFormLayout>
</template>
